import * as React from "react";
import { useState } from "react";
import { alpha } from "@mui/material/styles";
import { Box, Button, Card, CardContent, Chip, Divider, Grid, Stack, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import { AddToHomeScreen } from "react-pwa-add-to-homescreen";
import { useAppDispatch, useAppSelector } from "../../Hooks";
import { GetUserInfo } from "../../services/api-actions/user-action";
import { getUserInfo } from "../../store/user-process/selector";
import LocalStorageProvider from "../../utils/locaStorageProvider";
import { LocalStorageMethod } from "../../enums/enum";
import AddLic from "../../Components/dialog/lic/AddLic";
import { getPersonalInfo, getUserLicAddresses } from "../../store/integration-process/selector";
import valueIsNullOrUndefined from "../../utils/valueOrDefault";
import LicDialog from "../../Components/dialog/lic/LicDialog";
import QueryElectricalReceipt from "../../Components/dialog/lic/QueryElectricalReceipt";
import DeleteLic from "../../Components/dialog/lic/delete-lic";
import PageLayout from "../../Components/page-layout/PageLayout";
import { brand } from "../../theme";

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack direction="row" justifyContent="space-between" gap={2} sx={{ py: 1 }}>
      <Typography color="text.secondary">{label}</Typography>
      <Typography sx={{ fontWeight: 500, textAlign: "right" }}>{value || "—"}</Typography>
    </Stack>
  );
}

export default function Lic() {
  const [openDialogAddLic, setOpenDialogAddLic] = useState(false);
  const [selectLicDelete, setSelectLicDelete] = useState("");
  const [openDialogDeleteLic, setOpenDialogDeleteLic] = useState(false);
  const [openElectricalReceiptDialog, setOpenElectricalReceiptDialog] = useState(false);
  const dispatch = useAppDispatch();
  React.useEffect(() => {
    dispatch(GetUserInfo());
  }, [dispatch]);
  const personalInfo = useAppSelector(getPersonalInfo);
  const mainPersonalInfo = personalInfo.personsData?.find((x) => x.isMain === true);
  const userInfo = useAppSelector(getUserInfo);
  const userLic = useAppSelector(getUserLicAddresses);
  if (userInfo.lics?.length == 1) {
    LocalStorageProvider(userInfo.userId, { lic: userInfo.lics[0].lic, address: "" }, LocalStorageMethod.setItem);
  }
  let defaultLic = LocalStorageProvider(userInfo.userId, null, LocalStorageMethod.getItem);
  if (userInfo.lics?.length >= 1 && defaultLic == "") {
    LocalStorageProvider(userInfo.userId, { lic: userInfo.lics[0].lic, address: "" }, LocalStorageMethod.setItem);
  }
  if (defaultLic != "" && userInfo?.lics.find((x) => x.lic == defaultLic.lic) == undefined) {
    defaultLic = "";
  }
  const askDelete = (lic: string) => {
    setSelectLicDelete(lic);
    setOpenDialogDeleteLic(true);
  };
  const ownerName = [mainPersonalInfo?.lastName, mainPersonalInfo?.firstName, mainPersonalInfo?.middleName]
    .map((x) => valueIsNullOrUndefined(x, ""))
    .join(" ")
    .trim();
  const otherLics = userLic.filter((x) => x.lic != defaultLic?.lic && userInfo.lics.filter((z) => z.lic == x.lic).length > 0);

  const addButton = (
    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialogAddLic(true)}>
      Добавить лицевой счёт
    </Button>
  );

  return (
    <>
      <AddToHomeScreen />
      {defaultLic.lic !== undefined && defaultLic != "" ? (
        <PageLayout title="Мои лицевые счета" subtitle="Выбранный счёт используется во всех разделах кабинета" actions={addButton}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%", borderColor: brand.main, borderWidth: 2 }}>
                <CardContent>
                  <Chip label="Выбранный счёт" size="small" sx={{ bgcolor: alpha(brand.main, 0.12), color: brand.main, mb: 1 }} />
                  <Typography sx={{ fontSize: 24, fontWeight: 700 }}>{defaultLic?.lic}</Typography>
                  <Typography color="text.secondary">{personalInfo?.address?.fullAddress}</Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography color="text.secondary" sx={{ fontSize: 14 }}>
                    Задолженность на текущую дату
                  </Typography>
                  <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
                    {personalInfo.currentDebt !== undefined && personalInfo.currentDebt !== null
                      ? `${Number(personalInfo.currentDebt).toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽`
                      : "—"}
                  </Typography>
                  {mainPersonalInfo?.hasElectronicReceipts == true && (
                    <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 1.5, color: brand.success }}>
                      <MarkEmailReadOutlinedIcon fontSize="small" />
                      <Typography sx={{ fontSize: 14 }}>Электронная квитанция приходит на {mainPersonalInfo?.email}</Typography>
                    </Stack>
                  )}
                  <Stack direction={{ xs: "column", sm: "row" }} gap={1} sx={{ mt: 2.5 }}>
                    <Button variant="outlined" onClick={() => setOpenElectricalReceiptDialog(true)}>
                      Электронная квитанция
                    </Button>
                    {userInfo.lics?.length >= 1 && (
                      <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={() => askDelete(defaultLic?.lic)}>
                        Удалить счёт
                      </Button>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Информация о собственнике
                  </Typography>
                  <InfoRow label="ФИО" value={ownerName} />
                  <Divider />
                  <InfoRow label="Площадь помещения" value={personalInfo?.square ? `${personalInfo.square} м²` : ""} />
                  <Divider />
                  <InfoRow label="Зарегистрировано человек" value={personalInfo?.numberOfPersons} />
                  <Divider />
                  <InfoRow label="Телефон" value={[mainPersonalInfo?.firstPhoneNumber, mainPersonalInfo?.secondPhoneNumber].filter((x) => !!x).join(", ")} />
                  <Divider />
                  <InfoRow label="Эл. почта" value={mainPersonalInfo?.email} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {otherLics.length > 0 && (
            <>
              <Typography variant="h6" sx={{ mt: 4, mb: 1.5 }}>
                Другие лицевые счета
              </Typography>
              <Grid container spacing={2}>
                {otherLics.map((item) => (
                  <Grid item xs={12} md={6} key={item.lic}>
                    <Card sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography sx={{ fontSize: 20, fontWeight: 700 }}>{item?.lic}</Typography>
                        <Typography color="text.secondary">{item?.fullAddress}</Typography>
                        <Stack direction={{ xs: "column", sm: "row" }} gap={1} sx={{ mt: 2 }}>
                          <Button
                            variant="contained"
                            onClick={() => {
                              LocalStorageProvider(userInfo.userId, { lic: item?.lic, address: "" }, LocalStorageMethod.setItem);
                              window.location.reload();
                            }}
                          >
                            Сделать выбранным
                          </Button>
                          {userInfo.lics?.length > 1 && (
                            <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={() => askDelete(item?.lic)}>
                              Удалить
                            </Button>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          <LicDialog Open={openElectricalReceiptDialog} Close={setOpenElectricalReceiptDialog} Title="Электронная квитанция" Component={<QueryElectricalReceipt />} />
          <DeleteLic Open={openDialogDeleteLic} Close={setOpenDialogDeleteLic} Title={`Удалить лицевой счёт ${selectLicDelete}?`} Lic={selectLicDelete} UserId={userInfo.userId} />
        </PageLayout>
      ) : (
        <PageLayout title="Мои лицевые счета">
          <Card sx={{ textAlign: "center", py: 5, px: 2 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                mx: "auto",
                mb: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(brand.main, 0.1),
                color: brand.main,
              }}
            >
              <BadgeOutlinedIcon fontSize="large" />
            </Box>
            <Typography variant="h5">У вас пока нет лицевых счетов</Typography>
            <Typography color="text.secondary" sx={{ mt: 1, mb: 3, maxWidth: 480, mx: "auto" }}>
              Добавьте лицевой счёт, чтобы передавать показания, смотреть историю оплат и получать квитанции.
            </Typography>
            {addButton}
          </Card>
        </PageLayout>
      )}
      <AddLic Open={openDialogAddLic} Close={setOpenDialogAddLic} Title="Добавить лицевой счет" />
    </>
  );
}
